import { useLayoutEffect, useState } from "react";
import {
  Modal,
  Spin,
  Row,
  Col,
  Input,
  Select,
  Descriptions,
  Badge,
  Button,
} from "antd";
import {
  VENDOR_DOCUMENTS,
  APPROVED_REJECT_OPTION,
} from "../../../constants/constent";
import { postAPI, displayMessage } from "../../../utils/common";
import {
  UPDATE_VENDOR_DOCUMENT_STATUS,
  VENDOR_BY_ID,
} from "../../../constants/api";
import DocumentCard from "./DocumentCard";
import VendorDetailCard from "./VendorDetailCard";

function VendorDocumentModal({ open, vendorId, onClose, refreshList }) {
  const [vendor, setVendor] = useState(null);
  const [vendorDocs, setVendorDocs] = useState(null);
  const [docStatus, setDocStatus] = useState({});
  const [docRemarks, setDocRemarks] = useState({});
  const [loading, setLoading] = useState(false);

  useLayoutEffect(() => {
    if (vendorId) fetchVendorDocs(vendorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const fetchVendorDocs = (id) => {
    setLoading(true);
    postAPI(
      VENDOR_BY_ID,
      { id },
      (res) => {
        if (res.status) {
          const v = res.data;
          setVendor(v);
          setVendorDocs(v.documents);
          setDocStatus({
            doc_pan_card_is_approved: v.documents?.doc_pan_card_is_approved,
            doc_aadhar_is_approved: v.documents?.doc_aadhar_is_approved,
            doc_gst_certificate_is_approved:
              v.documents?.doc_gst_certificate_is_approved,
            doc_msme_certificate_is_approved:
              v.documents?.doc_msme_certificate_is_approved,
            doc_trade_license_is_approved:
              v.documents?.doc_trade_license_is_approved,
            doc_bank_passbook_is_approved:
              v.documents?.doc_bank_passbook_is_approved,
            doc_property_img_google_drive_link_is_approved:
              v.documents?.doc_property_img_google_drive_link_is_approved,
            doc_hotel_profile_is_approved:
              v.documents?.doc_hotel_profile_is_approved,
          });

          setDocRemarks({
            doc_pan_card_remark: v.documents?.doc_pan_card_remark,
            doc_aadhar_remark: v.documents?.doc_aadhar_remark,
            doc_gst_certificate_remark: v.documents?.doc_gst_certificate_remark,
            doc_msme_certificate_remark:
              v.documents?.doc_msme_certificate_remark,
            doc_trade_license_remark: v.documents?.doc_trade_license_remark,
            doc_bank_passbook_remark: v.documents?.doc_bank_passbook_remark,
            doc_property_img_google_drive_link_remark:
              v.documents?.doc_property_img_google_drive_link_remark,
            doc_hotel_profile_remark: v.documents?.doc_hotel_profile_remark,
          });

          setLoading(false);
        } else {
          setLoading(false);
        }
      },
      () => setLoading(false),
    );
  };

  const updateStatus = (key, value) => {
    setDocStatus((prev) => ({ ...prev, [key]: value }));
  };

  const updateRemark = (key, value) => {
    setDocRemarks((prev) => ({ ...prev, [key]: value }));
  };

  const saveVendorVerification = () => {
    setLoading(true);
    // Verification now covers document approval only; vendor settings moved
    // to the Quick Edit Vendor modal.
    const payload = {
      vendor_id: vendorId,
      ...docStatus,
      ...docRemarks,
    };

    postAPI(
      UPDATE_VENDOR_DOCUMENT_STATUS,
      payload,
      (res) => {
        if (res.status) {
          setLoading(false);
          displayMessage("success", res.message);
          refreshList();
          onClose();
        } else {
          setLoading(false);
          displayMessage("error", res.message);
        }
      },
      () => {
        setLoading(false);
        displayMessage("error", "Update failed");
      },
    );
  };

  return (
    <Modal
      open={open}
      title="Super Admin Verification"
      width={1400}
      loading={loading}
      destroyOnHidden={true}
      onCancel={() => {
        onClose();
      }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={saveVendorVerification}
        >
          Save Verification
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        {!loading && (
          <>
            {vendor && (
              <div style={{ marginBottom: 18 }}>
                <VendorDetailCard vendor={vendor} />
              </div>
            )}
            <Row gutter={[16, 16]}>
              <Col span={24}>
                {vendorDocs &&
                  (() => {
                    const STATUS_KEY =
                      "doc_property_img_google_drive_link_is_approved";
                    const REMARK_KEY =
                      "doc_property_img_google_drive_link_remark";

                    const status = docStatus[STATUS_KEY];
                    const remark = docRemarks[REMARK_KEY];

                    const getStatusColor = (value) => {
                      if (value === "approved") return "green";
                      if (value === "reject") return "red";
                      return "orange";
                    };

                    return (
                      <Descriptions bordered layout="vertical">
                        <Descriptions.Item label="PAN Card No">
                          {vendorDocs.doc_pan_card_no}
                        </Descriptions.Item>

                        <Descriptions.Item label="Aadhar No">
                          {vendorDocs.doc_aadhar_no}
                        </Descriptions.Item>

                        <Descriptions.Item label="Property IMG Drive Link">
                          <Badge.Ribbon
                            text={status?.toUpperCase()}
                            color={getStatusColor(status)}
                          >
                            <div>
                              {vendorDocs.doc_property_img_google_drive_link}
                            </div>
                          </Badge.Ribbon>
                        </Descriptions.Item>

                        <Descriptions.Item label="Property IMG Drive Link Status">
                          <div style={{ marginTop: 8 }}>
                            <Select
                              style={{ width: "100%" }}
                              value={status}
                              onChange={(v) => updateStatus(STATUS_KEY, v)}
                              options={APPROVED_REJECT_OPTION}
                            />

                            {status === "reject" && (
                              <Input.TextArea
                                rows={3}
                                placeholder="Enter rejection remark"
                                value={remark}
                                onChange={(e) =>
                                  updateRemark(REMARK_KEY, e.target.value)
                                }
                                style={{ marginTop: 8 }}
                              />
                            )}
                          </div>
                        </Descriptions.Item>
                      </Descriptions>
                    );
                  })()}
              </Col>

              {VENDOR_DOCUMENTS.map((doc) => (
                <DocumentCard
                  key={`vendor_doc_${doc.id}`}
                  doc={doc}
                  vendorDocs={vendorDocs}
                  docStatus={docStatus}
                  docRemarks={docRemarks}
                  onStatus={updateStatus}
                  onRemark={updateRemark}
                />
              ))}
            </Row>
          </>
        )}
      </Spin>
    </Modal>
  );
}

export default VendorDocumentModal;
